import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Beaker, Pencil, Plus, Trash2, AlertTriangle,
} from "lucide-react";
import {
  deleteSamplingRule,
  listSamplingRules,
  SAMPLING_SCOPES,
  SAMPLING_TRIGGERS,
  toggleSamplingRule,
  upsertSamplingRule,
  listSites,
  listClients,
  listFieldOverrides,
} from "@/lib/api";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";

const TRIGGER_LABEL = SAMPLING_TRIGGERS.reduce((a, t) => ((a[t.value] = t.label), a), {});
const SCOPE_LABEL   = SAMPLING_SCOPES.reduce((a, s) => ((a[s.value] = s.label), a), {});

/* ── Phase 6.2: site-check booleans always available as triggers ── */
const SITE_CHECK_TRIGGERS = [
  { key: "contamination_present",     label: "Site check · Contamination present",     kind: "boolean" },
  { key: "damage_pallets",            label: "Site check · Damage — pallets",          kind: "boolean" },
  { key: "damage_products",           label: "Site check · Damage — products",         kind: "boolean" },
  { key: "trailer_clean_confirmed",   label: "Site check · Trailer clean confirmed",   kind: "boolean" },
  { key: "compliance_temp_confirmed", label: "Site check · Compliance temp confirmed", kind: "boolean" },
  { key: "temp_set_confirmed",        label: "Site check · Temp set confirmed",        kind: "boolean" },
];
const BOOLEAN_KEYS = new Set(SITE_CHECK_TRIGGERS.map((s) => s.key));

const empty = (clientId) => ({
  client_id: clientId || "",
  site_id: "",
  name: "",
  scope: "customer",
  target_match: "any",
  target_value: "",
  trigger_type: "every_nth",
  frequency_n: 5,
  random_percent: "",
  ncr_threshold: "",
  ncr_window_days: 30,
  is_active: true,
  notes: "",
});

export default function SamplingRules() {
  const { assignments } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const defaultClient = viewAsClientId
    || assignments?.find((a) => a.client_id)?.client_id
    || "";

  const [rules, setRules]     = useState([]);
  const [sites, setSites]     = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tick, setTick]       = useState(0);
  const refresh = () => setTick((k) => k + 1);

  const [overrides, setOverrides] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [r, s, c, o] = await Promise.all([
        listSamplingRules({ clientId: viewAsClientId || null }),
        listSites(),
        listClients(),
        listFieldOverrides(viewAsClientId || null),
      ]);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setRules(r.data || []);
      setSites(s.data || []);
      setClients(c.data || []);
      setOverrides(o.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, tick]);

  // Phase 6.2: build the unified scope option list — legacy SAMPLING_SCOPES
  // first, then this tenant's QA-trigger-eligible FIELDS, then the always-
  // available site-check booleans, then "other" for free-text fall-back.
  const qaEligibleFieldKeys = overrides
    .filter((o) => o.scope === "field" && o.is_qa_trigger === true)
    .map((o) => o.field_key);
  const fieldByKey = FIELDS.reduce((a, f) => ((a[f.key] = f), a), {});
  const dynamicScopeOptions = [
    ...qaEligibleFieldKeys
      .filter((k) => fieldByKey[k])
      .map((k) => ({
        value: k,
        label: `Field · ${fieldByKey[k].label}`,
        kind: fieldByKey[k].component === "NumberField" ? "number" : "text",
        picklist: fieldByKey[k].optionsFromPicks || null,
      })),
    ...SITE_CHECK_TRIGGERS.map((t) => ({ value: t.key, label: t.label, kind: t.kind })),
    { value: "_other_", label: "Other (custom field key)", kind: "text" },
  ];
  const scopeMeta = (val) =>
    dynamicScopeOptions.find((o) => o.value === val) ||
    { value: val, label: SCOPE_LABEL[val] || val, kind: "text" };

  const onSave = async (values) => {
    const { error } = await upsertSamplingRule(values);
    if (error) { toast.error(error.message); return; }
    toast.success(values.id ? "Rule updated." : "Rule created.");
    setEditing(null);
    refresh();
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    const { error } = await deleteSamplingRule(rule.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Deleted.");
    refresh();
  };

  const onToggle = async (rule) => {
    const { error } = await toggleSamplingRule(rule.id, !rule.is_active);
    if (error) { toast.error(error.message); return; }
    refresh();
  };

  return (
    <div data-testid="sampling-rules-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Beaker className="h-6 w-6" /> Sampling regime
          </h1>
          <p className="text-sm text-slate-500">
            Rules that auto-flag bookings for sampling — driven by setup or NCR history.
          </p>
        </div>
        <Button onClick={() => setEditing(empty(defaultClient))} data-testid="sampling-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New rule
        </Button>
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            Rules <span className="text-sm font-normal text-slate-500 tabular-nums">({rules.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-3 py-6 text-sm text-slate-400">Loading…</div>
          ) : rules.length === 0 ? (
            <div className="px-3 py-10 text-center" data-testid="sampling-empty">
              <AlertTriangle className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No sampling rules yet — every booking goes unflagged.</p>
              <Button size="sm" className="mt-3" onClick={() => setEditing(empty(defaultClient))}>
                <Plus className="h-4 w-4 mr-1.5" /> Create first rule
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="w-1 text-right">&nbsp;</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id} data-testid={`sampling-row-${r.id}`}>
                    <TableCell className="font-medium text-sm">
                      {r.name}
                      {r.notes && <div className="text-xs text-slate-500 max-w-xs truncate">{r.notes}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] font-mono">{r.scope}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.target_match === "any" ? <em className="text-slate-400">any</em> :
                       r.target_match === "specific" ? <span>= <strong>{r.target_value}</strong></span> :
                       <span>⊃ <strong>{r.target_value}</strong></span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {TRIGGER_LABEL[r.trigger_type]}
                      {r.trigger_type === "every_nth" && <span className="text-slate-500"> · N={r.frequency_n}</span>}
                      {r.trigger_type === "random_percent" && <span className="text-slate-500"> · {r.random_percent}%</span>}
                      {r.trigger_type === "ncr_escalation" && (
                        <span className="text-slate-500"> · ≥{r.ncr_threshold} in {r.ncr_window_days}d</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.site?.name || <em className="text-slate-400">all sites</em>}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={r.is_active}
                        onCheckedChange={() => onToggle(r)}
                        data-testid={`sampling-toggle-${r.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(r)} data-testid={`sampling-edit-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => onDelete(r)} data-testid={`sampling-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RuleDialog
        key={editing?.id || "new"}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        values={editing || empty(viewAsClientId)}
        clients={clients}
        sites={sites}
        scopeOptions={dynamicScopeOptions}
        onSave={onSave}
      />
    </div>
  );
}

function RuleDialog({ open, onOpenChange, values, onSave, clients, sites, scopeOptions = [] }) {
  // Parent remounts on values.id change via key; init state once from props.
  const [v, setV] = useState(values || {});
  if (!values) return null;
  const set = (k, val) => setV((x) => ({ ...x, [k]: val }));
  const filteredSites = sites.filter((s) => !v.client_id || s.client_id === v.client_id);

  const submit = (e) => {
    e.preventDefault();
    if (!v.name)   { toast.error("Name required"); return; }
    if (!v.client_id) { toast.error("Client required"); return; }
    if (v.trigger_type === "every_nth" && !v.frequency_n) {
      toast.error("Frequency N required"); return;
    }
    if (v.trigger_type === "random_percent" && (v.random_percent === "" || v.random_percent == null)) {
      toast.error("Percent required"); return;
    }
    if (v.trigger_type === "ncr_escalation" && !v.ncr_threshold) {
      toast.error("NCR threshold required"); return;
    }
    onSave(v);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sampling-rule-dialog">
        <DialogHeader>
          <DialogTitle>{v.id ? "Edit sampling rule" : "New sampling rule"}</DialogTitle>
          <DialogDescription>
            Define WHO/WHAT to sample and WHEN to trigger.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Rule name *">
              <Input value={v.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="sampling-name-input" />
            </Field>
            <Field label="Client *">
              <Select value={v.client_id || ""} onValueChange={(x) => set("client_id", x)}>
                <SelectTrigger data-testid="sampling-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Site (optional — leave empty for all sites)">
              <Select value={v.site_id || "_none_"} onValueChange={(x) => set("site_id", x === "_none_" ? "" : x)}>
                <SelectTrigger><SelectValue placeholder="All sites" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">All sites</SelectItem>
                  {filteredSites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Scope *">
              <Select value={v.scope} onValueChange={(x) => { set("scope", x); set("target_value", ""); }}>
                <SelectTrigger data-testid="sampling-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[420px]">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">Built-in</div>
                  {SAMPLING_SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  {scopeOptions.length > 0 && (
                    <>
                      <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wider text-slate-500">Custom triggers (this tenant)</div>
                      {scopeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </>
                  )}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Target match">
              <Select value={v.target_match} onValueChange={(x) => set("target_match", x)}>
                <SelectTrigger data-testid="sampling-match-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any (all)</SelectItem>
                  <SelectItem value="specific">Specific value (exact)</SelectItem>
                  <SelectItem value="contains">Contains text (case-insensitive)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {v.target_match !== "any" && (() => {
              const meta = scopeOptions.find((o) => o.value === v.scope);
              const isBool = meta?.kind === "boolean" || BOOLEAN_KEYS.has(v.scope);
              return (
                <Field label="Target value">
                  {isBool ? (
                    <Select value={v.target_value || ""} onValueChange={(x) => set("target_value", x)}>
                      <SelectTrigger data-testid="sampling-target-select"><SelectValue placeholder="Pick Yes / No" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes (true)</SelectItem>
                        <SelectItem value="false">No (false)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={v.target_value || ""} onChange={(e) => set("target_value", e.target.value)}
                           placeholder={meta?.kind === "number" ? "e.g. 30000" : "e.g. Acme Logistics"}
                           data-testid="sampling-target-input" />
                  )}
                </Field>
              );
            })()}
          </div>

          <div className="rounded-md border bg-slate-50 p-3 space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Trigger</div>
            <Field label="Trigger type *">
              <Select value={v.trigger_type} onValueChange={(x) => set("trigger_type", x)}>
                <SelectTrigger data-testid="sampling-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SAMPLING_TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            {v.trigger_type === "every_nth" && (
              <Field label="N (every Nth matching booking) *">
                <Input type="number" min={1} value={v.frequency_n || ""} onChange={(e) => set("frequency_n", e.target.value)} data-testid="sampling-freq-input" />
              </Field>
            )}
            {v.trigger_type === "random_percent" && (
              <Field label="Percent chance (0–100) *">
                <Input type="number" min={0} max={100} value={v.random_percent || ""} onChange={(e) => set("random_percent", e.target.value)} data-testid="sampling-percent-input" />
              </Field>
            )}
            {v.trigger_type === "ncr_escalation" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="NCR threshold *">
                  <Input type="number" min={1} value={v.ncr_threshold || ""} onChange={(e) => set("ncr_threshold", e.target.value)} data-testid="sampling-ncr-thr-input" />
                </Field>
                <Field label="Window (days) *">
                  <Input type="number" min={1} value={v.ncr_window_days || 30} onChange={(e) => set("ncr_window_days", e.target.value)} data-testid="sampling-ncr-win-input" />
                </Field>
              </div>
            )}
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={v.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Internal QA justification…" />
          </Field>

          <div className="flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-2">
              <Switch checked={v.is_active !== false} onCheckedChange={(x) => set("is_active", x)} data-testid="sampling-active-switch" />
              <Label className="text-xs">Active</Label>
            </div>
            <DialogFooter className="m-0 p-0 gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" data-testid="sampling-save-btn">{v.id ? "Save" : "Create rule"}</Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
